import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings as SettingsIcon, Volume2, VolumeX, Play } from 'lucide-react';
import { toast } from 'sonner';
import { AVAILABLE_SOUNDS, ALERT_TYPES_WITH_SOUNDS, previewSound, DEFAULT_SOUND_SETTINGS } from '../../lib/sounds';

export function SoundAlertSection({ darkMode, practice, updateSoundSettings }) {
  const [soundDialogOpen, setSoundDialogOpen] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(true);
  const [soundVolume, setSoundVolume] = useState(0.7);
  const [soundAssignments, setSoundAssignments] = useState({});

  useEffect(() => {
    if (practice) {
      setSoundsEnabled(practice.sounds_enabled !== false);
      setSoundVolume(practice.sound_volume ?? 0.7);
      setSoundAssignments(practice.sound_assignments || DEFAULT_SOUND_SETTINGS.sound_assignments);
    }
  }, [practice]);

  return (
    <div className="glass-panel rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-bold text-slate-800">Sound Alerts</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Audio notifications</span>
          <Dialog open={soundDialogOpen} onOpenChange={setSoundDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="configure-sounds-button" variant="outline" size="sm">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Configure Sounds
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Sound Alert Configuration</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Master Switch */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {soundsEnabled ? <Volume2 className="w-5 h-5 text-dp-600" /> : <VolumeX className="w-5 h-5 text-slate-400" />}
                    <div>
                      <span className="font-semibold text-slate-700">Sound Alerts</span>
                      <p className="text-xs text-slate-500">Play sounds when alerts appear</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSoundsEnabled(!soundsEnabled)}
                    data-testid="toggle-sounds-enabled"
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                      soundsEnabled ? 'bg-dp-600' : 'bg-slate-300'
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
                      soundsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Volume Control */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="font-semibold text-slate-700 mb-3 block">Volume</Label>
                  <div className="flex items-center gap-4">
                    <VolumeX className="w-4 h-4 text-slate-400" />
                    <input
                      type="range" min="0" max="1" step="0.1"
                      value={soundVolume}
                      onChange={(e) => setSoundVolume(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
                      data-testid="sound-volume-slider"
                    />
                    <Volume2 className="w-4 h-4 text-slate-600" />
                    <span className="text-sm text-slate-600 w-12 text-right">{Math.round(soundVolume * 100)}%</span>
                  </div>
                </div>

                {/* Sound Assignments */}
                <div>
                  <Label className="font-semibold text-slate-700 mb-3 block">Sound Assignments</Label>
                  <p className="text-sm text-slate-500 mb-4">Choose a sound for each type of alert</p>
                  
                  <div className="space-y-3">
                    {ALERT_TYPES_WITH_SOUNDS.map((alertType) => (
                      <div key={alertType.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                        <div className="flex-1">
                          <span className="font-medium text-slate-700 text-sm">{alertType.name}</span>
                          <p className="text-xs text-slate-500">{alertType.description}</p>
                        </div>
                        <Select
                          value={soundAssignments[alertType.id] || 'gentle_chime'}
                          onValueChange={(value) => setSoundAssignments(prev => ({ ...prev, [alertType.id]: value }))}
                        >
                          <SelectTrigger className="w-44" data-testid={`sound-select-${alertType.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AVAILABLE_SOUNDS.map((sound) => (
                              <SelectItem key={sound.id} value={sound.id}>{sound.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => previewSound(soundAssignments[alertType.id] || 'gentle_chime')}
                          disabled={soundAssignments[alertType.id] === 'none'}
                          data-testid={`preview-sound-${alertType.id}`}
                          className="h-9 w-9"
                        >
                          <Play className="w-4 h-4 text-dp-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={async () => {
                    const result = await updateSoundSettings({
                      sounds_enabled: soundsEnabled,
                      sound_volume: soundVolume,
                      sound_assignments: soundAssignments
                    });
                    if (result.success) {
                      toast.success('Sound settings saved');
                      setSoundDialogOpen(false);
                    } else {
                      toast.error(result.error);
                    }
                  }} 
                  className="w-full bg-dp-600 hover:bg-dp-700 text-white"
                  data-testid="save-sound-settings"
                >
                  Save Sound Settings
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Quick Status Display */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
        <button
          onClick={async () => {
            const newValue = !soundsEnabled;
            setSoundsEnabled(newValue);
            const result = await updateSoundSettings({ sounds_enabled: newValue });
            if (result.success) {
              toast.success(newValue ? 'Sound alerts enabled' : 'Sound alerts disabled');
            } else {
              toast.error(result.error);
            }
          }}
          data-testid="quick-toggle-sounds"
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
            soundsEnabled ? 'bg-dp-600' : 'bg-slate-300'
          }`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
            soundsEnabled ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
        <div className="flex-1">
          <span className="text-slate-700 text-sm font-medium">
            {soundsEnabled ? 'Sound alerts are enabled' : 'Sound alerts are disabled'}
          </span>
          {soundsEnabled && <p className="text-xs text-slate-500">Volume: {Math.round(soundVolume * 100)}%</p>}
        </div>
        {soundsEnabled && <Volume2 className="w-5 h-5 text-dp-600" />}
      </div>
    </div>
  );
}
